import {
  actionClassForStatus,
  buildGenericRiskDrawer,
  cloneDeep,
  createDefaultRiskForm,
  enrich_appetite_row,
  MAX_APPETITE_SCALE,
  RECORD_ICON_MAP,
  tableClassForStatus,
  toneClassForStatus
} from './risks_dashboard_helpers';

function departmentFromCategory(category, fallback = '') {
  const department_map = {
    Finance: 'Finance',
    Procurement: 'Procurement',
    Compliance: 'Legal',
    'IT security': 'IT Security',
    Operational: 'IT',
    People: 'HR',
    'Data privacy': 'Legal',
    Strategic: 'Finance',
    Reputational: 'Finance',
    Financial: 'Finance'
  };
  const mapped_department = Reflect.get(department_map, category);
  const department_name = mapped_department || fallback || category || 'Finance';
  return department_name;
}

function active_risk_row(rows, risk_ref) {
  const risk_row = rows.find((item) => item.ref === risk_ref);
  return risk_row;
}

function active_catalog_drawer(catalog, risk_ref) {
  const catalog_drawer = Reflect.get(catalog || {}, risk_ref) || null;
  return catalog_drawer;
}

function resolved_risk_drawer(row, catalog_drawer) {
  if (!row) return null;
  const risk_drawer = row.drawer || catalog_drawer;
  return risk_drawer;
}

function register_card_value(card, total_rows, escalated_count, high_residual_count, above_appetite_count) {
  const card_value_map = {
    'Risks in register': String(total_rows),
    'High residual': String(high_residual_count),
    'Above appetite': String(above_appetite_count),
    Escalated: String(escalated_count)
  };
  const next_value = Reflect.get(card_value_map, card.label);
  return next_value ? { ...card, value: next_value } : card;
}

function update_department_card(card, row, residual, status_label) {
  const updated_peak = Math.max(Number(card.peak), residual);
  const updated_card = {
    ...card,
    count: card.count + 1,
    peak: updated_peak,
    escalated: status_label === 'Escalated' ? card.escalated + 1 : card.escalated,
    badge: updated_peak >= 8 ? 'Medium' : 'Low',
    rows: [{ label: row.risk, value: residual, color: residual >= 8 ? 'orange' : 'green' }, ...card.rows]
  };
  return updated_card;
}

export const risks_dashboard_computed = {
  active_risk_drawer() {
    if (!this.active_risk_drawer_key) return null;
    const row = active_risk_row(this.risk_rows, this.active_risk_drawer_key);
    const catalog_drawer = active_catalog_drawer(this.risk_drawer_catalog, this.active_risk_drawer_key);
    const active_risk_drawer = resolved_risk_drawer(row, catalog_drawer);
    return active_risk_drawer;
  }
};

export const risks_dashboard_methods = {
  sync_overlay_state() { document.body.classList.toggle('risks-overlay-open', this.show_risk_modal || Boolean(this.active_risk_drawer_key)); },
  update_appetite_row(area, updater) {
    const row_index = this.appetite_rows.findIndex((row) => row.area === area);
    if (row_index === -1) return;
    const current_row = Reflect.get(this.appetite_rows, row_index);
    this.appetite_rows.splice(row_index, 1, enrich_appetite_row(updater({ area: current_row.area, peak: current_row.peak, appetite: current_row.appetite })));
  },
  set_tab(tab_id) { this.active_tab = tab_id; this.close_risk_drawer(); },
  decrease_peak(area) { this.update_appetite_row(area, (row) => ({ ...row, peak: Math.max(1, row.peak - 1) })); },
  increase_appetite(area) { this.update_appetite_row(area, (row) => ({ ...row, appetite: Math.min(MAX_APPETITE_SCALE, row.appetite + 1) })); },
  handle_keydown(event) { if (event.key === 'Escape') { if (this.show_risk_modal) this.close_risk_modal(); else if (this.active_risk_drawer_key) this.close_risk_drawer(); } },
  drawer_tone_class(tone) { const tone_map = { teal: 'is-green', green: 'is-green', orange: 'is-orange', amber: 'is-orange', red: 'is-red', slate: 'is-slate' }; const tone_class = Reflect.get(tone_map, tone) || 'is-slate'; return tone_class; },
  drawer_icon_for_type(type) { const icon = Reflect.get(RECORD_ICON_MAP, type) || 'bi-circle'; return icon; },
  drawer_status_class(status) { return toneClassForStatus(status); },
  action_status_class(status) { return actionClassForStatus(status); },
  open_risk_drawer(row_or_ref) {
    const risk_ref = typeof row_or_ref === 'string' ? row_or_ref : row_or_ref?.ref;
    if (!risk_ref) return;
    this.show_risk_modal = false;
    this.active_risk_drawer_key = risk_ref;
    this.sync_overlay_state();
    this.$nextTick(() => { const drawer_body = this.$refs.riskDrawerBody; if (drawer_body) drawer_body.scrollTop = 0; });
  },
  open_risk_by_label(risk_label) { const risk_row = this.risk_rows.find((row) => row.risk === risk_label); if (risk_row) this.open_risk_drawer(risk_row); },
  close_risk_drawer() { this.active_risk_drawer_key = null; this.sync_overlay_state(); },
  open_risk_modal() { this.active_risk_drawer_key = null; this.risk_form = createDefaultRiskForm(); this.show_risk_modal = true; this.sync_overlay_state(); },
  close_risk_modal() { this.show_risk_modal = false; this.sync_overlay_state(); },
  edit_active_risk() { if (!this.active_risk_drawer_key) return; },
  escalate_active_risk() { if (!this.active_risk_drawer) return; },
  create_risk() {
    const next_id = this.risk_rows.length + 1;
    const inherent = this.risk_form.inherent_likelihood * this.risk_form.inherent_impact;
    const residual = this.risk_form.residual_likelihood * this.risk_form.residual_impact;
    const status_label = this.risk_form.status;
    const row = { ref: `R-${String(next_id).padStart(2, '0')}`, risk: this.risk_form.risk || 'Untitled risk', category: this.risk_form.category, owner: this.risk_form.owner || 'Unassigned', inherent, residual, trend: residual, status: { label: status_label, class: tableClassForStatus(status_label) } };
    const risk_drawer = buildGenericRiskDrawer(row, cloneDeep(this.risk_form));
    Reflect.set(row, 'drawer', risk_drawer);
    this.risk_rows.unshift(row);
    this.tabs = this.tabs.map((tab) => tab.id === 'register' ? { ...tab, count: this.risk_rows.length } : tab);
    const escalated_count = this.risk_rows.filter((item) => item.status.label === 'Escalated').length;
    const high_residual_count = this.risk_rows.filter((item) => Number(item.residual) >= 15).length;
    const above_appetite_count = this.risk_rows.filter((item) => Number(item.residual) > 10).length;
    this.register_cards = this.register_cards.map((card) => register_card_value(
      card,
      this.risk_rows.length,
      escalated_count,
      high_residual_count,
      above_appetite_count
    ));
    const department_name = departmentFromCategory(this.risk_form.category, this.risk_form.department);
    const card_index = this.department_cards.findIndex((card) => card.dept === department_name);
    if (card_index !== -1) {
      const card = Reflect.get(this.department_cards, card_index);
      this.department_cards.splice(card_index, 1, update_department_card(card, row, residual, status_label));
    }
    this.close_risk_modal();
  }
};

export function risks_dashboard_mounted() {
  document.addEventListener('keydown', this.handle_keydown);
  this.sync_overlay_state();
}

export function risks_dashboard_before_unmount() {
  document.removeEventListener('keydown', this.handle_keydown);
  document.body.classList.remove('risks-overlay-open');
}
